import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    Checkbox,
    FormControlLabel,
    Alert,
    AlertTitle,
} from "@mui/material";
import { Warning, Delete } from "@mui/icons-material";
import { usePendingQueue } from "hooks/usePendingQueue";
import { useAuth } from "store/AuthProvider";
import { enqueueSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import {
    initIndexedDbIfMissingStore,
    recoverIndexedDbIfMissingStoreError,
} from "utils/indexedDbRecovery";

interface CachePurgeDialogProps {
    open: boolean;
    onClose: () => void;
}

/**
 * E2 & E3: 清除缓存对话框
 * 
 * E2: 有待处理数据 - 严格多重确认
 * E3: 数据已同步 - 单一确认
 */
export const CachePurgeDialog: React.FC<CachePurgeDialogProps> = ({ open, onClose }) => {
    const navigate = useNavigate();
    const auth = useAuth();
    const { getPendingDataInfo } = usePendingQueue();
    const offlineDB = initIndexedDbIfMissingStore("offline");

    const [pendingCount, setPendingCount] = React.useState(0);
    const [failedCount, setFailedCount] = React.useState(0);
    const [isLoading, setIsLoading] = React.useState(true);
    
    // E2: 多重确认状态
    const [confirmCheck, setConfirmCheck] = React.useState(false);
    const [confirmText, setConfirmText] = React.useState("");

    // 检查待处理数据
    React.useEffect(() => {
        if (open) {
            setIsLoading(true);
            getPendingDataInfo().then(info => {
                setPendingCount(info.pendingCount);
                setFailedCount(info.failedCount);
                setIsLoading(false);
            }).catch(async (error) => {
                const didRecover = await recoverIndexedDbIfMissingStoreError(error);
                if (didRecover) {
                    try {
                        const recoveredInfo = await getPendingDataInfo();
                        setPendingCount(recoveredInfo.pendingCount);
                        setFailedCount(recoveredInfo.failedCount);
                        setIsLoading(false);
                        return;
                    } catch (retryError) {
                        console.error("Retry getPendingDataInfo after DB reinit failed:", retryError);
                    }
                }
                setPendingCount(0);
                setFailedCount(0);
                setIsLoading(false);
            });
        } else {
            // 重置状态
            setConfirmCheck(false);
            setConfirmText("");
        }
    }, [open, getPendingDataInfo]);

    const hasPendingData = pendingCount > 0 || failedCount > 0;

    /**
     * 执行清除缓存
     */
    const handlePurgeCache = async () => {
        try {
            const currentUser = auth.getLastCachedUser();
            const timestamp = new Date().toISOString();

            // 审计日志
            console.warn("ADMIN ACTION: Manual cache purge initiated", {
                type: hasPendingData ? "E2_WITH_PENDING_DATA" : "E3_ALL_SYNCED",
                timestamp,
                userId: currentUser?.id || "unknown",
                userName: currentUser?.name || "unknown",
                pendingCount,
                failedCount,
                recordsLost: pendingCount + failedCount,
            });

            // 清除所有 IndexedDB 数据
            try {
                await offlineDB.clear();
            } catch (error) {
                const didRecover = await recoverIndexedDbIfMissingStoreError(error);
                if (!didRecover) {
                    throw error;
                }
                await offlineDB.clear();
            }
            
            // 清除所有 localStorage
            localStorage.clear();

            // 显示成功消息
            if (hasPendingData) {
                enqueueSnackbar(
                    `緩存已清除。${pendingCount + failedCount} 條未同步數據已永久刪除。`,
                    { variant: "warning", autoHideDuration: 5000 }
                );
            } else {
                enqueueSnackbar(
                    "緩存已清除。請重新登錄以加載工作站數據。",
                    { variant: "info", autoHideDuration: 3000 }
                );
            }

            // 关闭对话框
            onClose();

            // 强制登出
            auth.logout();

            // 延迟跳转，让用户看到提示消息
            setTimeout(() => {
                navigate("/signin", { replace: true });
            }, 500);

        } catch (error) {
            console.error("Failed to purge cache:", error);
            enqueueSnackbar("清除緩存失敗，請稍後重試", { variant: "error" });
        }
    };

    // E2: 有待处理数据 - 严格多重确认
    if (hasPendingData) {
        const canConfirm = confirmCheck && confirmText === "PURGE DATA";

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle
                    sx={{
                        backgroundColor: "error.main",
                        color: "white",
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                    }}
                >
                    <Warning />
                    <Typography variant="h6" component="div">
                        危險操作警告
                    </Typography>
                </DialogTitle>

                <DialogContent sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                        重置緩存與重新同步數據
                    </Typography>

                    <Alert severity="error" sx={{ my: 2 }}>
                        <AlertTitle sx={{ fontWeight: 600 }}>
                            您有 {pendingCount + failedCount} 個未同步的旗袋包
                        </AlertTitle>
                        <Typography variant="body2">
                            清除緩存將<strong>永久刪除</strong>這些數據且<strong>無法恢復</strong>。
                            此操作將被記錄。
                        </Typography>
                    </Alert>

                    <Box
                        sx={{
                            p: 2,
                            my: 2,
                            backgroundColor: "rgba(211, 47, 47, 0.08)",
                            borderRadius: 2,
                            border: "1px solid rgba(211, 47, 47, 0.3)",
                        }}
                    >
                        {pendingCount > 0 && (
                            <Typography variant="body2" gutterBottom>
                                待同步數據: <strong>{pendingCount}</strong> 條
                            </Typography>
                        )}
                        {failedCount > 0 && (
                            <Typography variant="body2" gutterBottom>
                                同步失敗: <strong>{failedCount}</strong> 條
                            </Typography>
                        )}
                        <Typography variant="body2" color="error" sx={{ mt: 1, fontWeight: 600 }}>
                            將清除: <strong>{pendingCount + failedCount}</strong> 條記錄
                        </Typography>
                    </Box>

                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 3 }}>
                        <strong>請完成以下確認步驟：</strong>
                    </Typography>

                    <Box mt={2}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={confirmCheck}
                                    onChange={(e) => setConfirmCheck(e.target.checked)}
                                    color="error"
                                />
                            }
                            label={
                                <Typography variant="body2">
                                    我理解此操作將永久刪除 <strong>{pendingCount + failedCount}</strong> 個未同步的旗袋包
                                </Typography>
                            }
                        />

                        <Box mt={2}>
                            <Typography variant="body2" gutterBottom>
                                請輸入 <strong>PURGE DATA</strong> 以確認（區分大小寫）：
                            </Typography>
                            <TextField
                                fullWidth
                                value={confirmText}
                                onChange={(e) => setConfirmText(e.target.value)}
                                placeholder="PURGE DATA"
                                variant="outlined"
                                size="small"
                                sx={{ mt: 1 }}
                                error={confirmText.length > 0 && confirmText !== "PURGE DATA"}
                                helperText={
                                    confirmText.length > 0 && confirmText !== "PURGE DATA"
                                        ? "請完全輸入 PURGE DATA（大寫）"
                                        : ""
                                }
                            />
                        </Box>
                    </Box>

                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                        清除後將清除所有緩存數據、強制登出，並需要重新登錄以加載工作站數據。
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={onClose} color="inherit">
                        取消
                    </Button>
                    <Button
                        onClick={handlePurgeCache}
                        variant="contained"
                        color="error"
                        disabled={!canConfirm || isLoading}
                        startIcon={<Delete />}
                    >
                        清除緩存並登出
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    // E3: 所有数据已同步 - 单一确认
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 2 }
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                }}
            >
                <Warning color="warning" />
                <Typography variant="h6" component="div">
                    重置緩存與重新同步數據
                </Typography>
            </DialogTitle>

            <DialogContent sx={{ mt: 1 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                    <AlertTitle>✓ 所有數據已同步</AlertTitle>
                    <Typography variant="body2">
                        清除緩存將登出您的帳戶，並需要重新驗證以重新加載工作站數據。
                    </Typography>
                </Alert>

                <Typography variant="body2" color="text.secondary">
                    此操作將：
                </Typography>
                <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2.5 }}>
                    <Typography component="li" variant="body2" color="text.secondary">
                        清除所有本地緩存數據
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                        強制您登出
                    </Typography>
                    <Typography component="li" variant="body2" color="text.secondary">
                        需要重新登錄以加載工作站數據
                    </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                    注意：無論是否有網絡連接，此操作都可以執行。
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={onClose} color="inherit" autoFocus>
                    取消
                </Button>
                <Button
                    onClick={handlePurgeCache}
                    variant="contained"
                    color="warning"
                    disabled={isLoading}
                    startIcon={<Delete />}
                    sx={{
                        backgroundColor: "warning.main",
                        "&:hover": {
                            backgroundColor: "warning.dark",
                        },
                    }}
                >
                    清除緩存並登出
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CachePurgeDialog;

